import { type MouseEvent, type ReactNode, useState } from "react";

import type { Block, Inline, Library } from "../help/parse";
import { parseInline } from "../help/parse";
import { navigate } from "../router";

/**
 * A help article's body, rendered to React ELEMENTS from the blocks `help/parse.ts` reads.
 * No `dangerouslySetInnerHTML`, as everywhere in this app (`RichText.tsx` says why); the
 * articles are ours, but a renderer that cannot run markup cannot be talked into it later.
 *
 * Not `RichText`: that renders what people type into a stage, live checkboxes and all, and
 * flattens nesting (CLAUDE.md, Formatted instructions). Articles need headings, nested lists,
 * screenshots and links between articles, and none of those belong in a stage's instructions.
 */

/** Opens another article in place; a modified click opens it however the browser would. */
export function openArticle(e: MouseEvent, id: string): void {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  navigate({ kind: "help", id, screen: null });
}

export function ArticleLink({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  return (
    <a href={`/help/${id}`} className={className} onClick={(e) => openArticle(e, id)}>
      {children}
    </a>
  );
}

function inline(nodes: readonly Inline[], lib: Library, key: string): ReactNode[] {
  return nodes.map((n, i) => {
    const k = `${key}-${i}`;
    switch (n.t) {
      case "text":
        return n.v;
      case "code":
        return <code key={k}>{n.v}</code>;
      case "strong":
        return <strong key={k}>{inline(n.children, lib, k)}</strong>;
      case "em":
        return <em key={k}>{inline(n.children, lib, k)}</em>;
      case "link":
        return (
          <a key={k} href={n.href} target="_blank" rel="noreferrer noopener">
            {n.label}
          </a>
        );
      case "ref":
        // An article not written yet reads as its label rather than a link to nowhere.
        return lib.byId.has(n.id) ? (
          <ArticleLink key={k} id={n.id}>
            {n.label}
          </ArticleLink>
        ) : (
          <span key={k} className="help-ref-missing" title="Not written yet">
            {n.label}
          </span>
        );
    }
  });
}

export function Inlines({ text, lib }: { text: string; lib: Library }) {
  return <>{inline(parseInline(text), lib, "i")}</>;
}

/**
 * A screenshot where the article's `> Screenshot:` line is, captioned. One that has not been
 * copied in yet -- or fails to load -- is its caption alone: never a broken-image icon.
 */
function Shot({ name, caption, lib, sizes }: { name: string | null; caption: string; lib: Library; sizes: Sizes }) {
  const [failed, setFailed] = useState(false);
  const size = name ? sizes[name] : undefined;
  const show = name !== null && size !== undefined && !failed;
  return (
    <figure className={show ? "help-shot" : "help-shot pending"}>
      {show ? (
        <a href={`/help/${name}.webp`} target="_blank" rel="noreferrer" title="Open full size">
          <img
            src={`/help/${name}.webp`}
            width={size[0]}
            height={size[1]}
            alt={caption}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        </a>
      ) : null}
      <figcaption>
        {show ? null : <span className="help-shot-label">Screenshot to come</span>}
        <Inlines text={caption} lib={lib} />
      </figcaption>
    </figure>
  );
}

type Sizes = Readonly<Record<string, readonly [number, number]>>;

function Blocks({ blocks, lib, sizes }: { blocks: readonly Block[]; lib: Library; sizes: Sizes }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "heading":
            return b.level <= 2 ? (
              <h2 key={i} className="help-h2" id={b.anchor}>
                {b.text}
              </h2>
            ) : (
              <h3 key={i} className="help-h3" id={b.anchor}>
                {b.text}
              </h3>
            );
          case "para":
            return (
              <p key={i}>
                <Inlines text={b.text} lib={lib} />
              </p>
            );
          case "note":
            return (
              <blockquote key={i} className="help-note">
                <Inlines text={b.text} lib={lib} />
              </blockquote>
            );
          case "shot":
            return <Shot key={i} name={b.name} caption={b.caption} lib={lib} sizes={sizes} />;
          case "list": {
            const items = b.items.map((item, j) => (
              <li key={j}>
                <Inlines text={item.text} lib={lib} />
                {item.children.length > 0 ? <Blocks blocks={item.children} lib={lib} sizes={sizes} /> : null}
              </li>
            ));
            return b.ordered ? (
              <ol key={i} start={b.start === 1 ? undefined : b.start}>
                {items}
              </ol>
            ) : (
              <ul key={i}>{items}</ul>
            );
          }
        }
      })}
    </>
  );
}

/**
 * The body without its `## Related` section, which the Help screen draws from the front matter
 * as a row of links instead (titles taken from the articles themselves, so they cannot drift).
 */
export function splitRelated(blocks: readonly Block[]): { body: Block[]; relatedBlocks: Block[] } {
  const at = blocks.findIndex((b) => b.kind === "heading" && b.text.trim().toLowerCase() === "related");
  if (at < 0) return { body: [...blocks], relatedBlocks: [] };
  const end = blocks.findIndex((b, i) => i > at && b.kind === "heading" && b.level <= 2);
  return {
    body: [...blocks.slice(0, at), ...(end < 0 ? [] : blocks.slice(end))],
    relatedBlocks: blocks.slice(at + 1, end < 0 ? undefined : end),
  };
}

export function ArticleBody({ blocks, lib, sizes }: { blocks: readonly Block[]; lib: Library; sizes: Sizes }) {
  return (
    <div className="help-body">
      <Blocks blocks={blocks} lib={lib} sizes={sizes} />
    </div>
  );
}
